import { RulebookPage } from "@/components/RulebookPage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export function BasicTerms() {
  return (
    <RulebookPage title="Basic Terms">
      <h2 className="text-xl font-semibold mt-6 mb-2">Core Stats</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Every character has four core statistics:
      </p>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Hit Points (HP)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Humans can take a few light hits before going down! Everyone begins
          the game with 3 HP, and can reach a maximum of 8 HP. HP can be
          restored by resting, eating and drinking, or through magical spells
          and potions. If you reach 0 HP, you begin Bleeding Out, and if you
          Bleed Out for 5 minutes, you die.
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Armour Points (AP)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Armour Points are like extra Hit Points that sit on top of your
          regular HP. Damage is taken from your AP before you lose any HP. AP
          can be restored after a battle by taking at least 1 minute of roleplay
          to readjust, fix, and polish up your armour.
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Magic Points (MP)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Every human has a spark of magical potential within them, and begins
          with 1 MP. A Warlock can hold a maximum of 8 MP (13 for an Ultimate
          Warlock). MP is used up by casting spells and crafting items, and can
          be restored through rest, or by completing special challenges for your
          Patrons.
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Wealth Level (WL)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Represents your standing in society, and the coin you begin each game
          with. Wealthier characters are able to afford fancier armour, fund the
          purchase and creation of powerful items, and have access to events
          that their lower-class companions are not invited to. Everyone begins
          at Wealth Level 1 (Peasant).
        </CardContent>
      </Card>

      <div className="overflow-x-auto mb-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Stat
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Abbr.
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Base
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Max
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Ultimate
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-3 py-2 border-t border-border">Hit Points</td>
              <td className="px-3 py-2 border-t border-border">HP</td>
              <td className="px-3 py-2 border-t border-border">3</td>
              <td className="px-3 py-2 border-t border-border">8</td>
              <td className="px-3 py-2 border-t border-border">13</td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">
                Armour Points
              </td>
              <td className="px-3 py-2 border-t border-border">AP</td>
              <td className="px-3 py-2 border-t border-border">0</td>
              <td className="px-3 py-2 border-t border-border">4</td>
              <td className="px-3 py-2 border-t border-border">4</td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">Magic Points</td>
              <td className="px-3 py-2 border-t border-border">MP</td>
              <td className="px-3 py-2 border-t border-border">1</td>
              <td className="px-3 py-2 border-t border-border">8</td>
              <td className="px-3 py-2 border-t border-border">13</td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">Wealth Level</td>
              <td className="px-3 py-2 border-t border-border">WL</td>
              <td className="px-3 py-2 border-t border-border">1</td>
              <td className="px-3 py-2 border-t border-border">5</td>
              <td className="px-3 py-2 border-t border-border">5</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-sm text-muted-foreground mb-3">
        Only Ultimate Warlocks are able to attain the superhuman power in the
        final column. Upon attaining the title of Ultimate Warlock, your HP and
        MP automatically increase to 13, and your wealth level immediately rises
        to 5 (Royalty). See &quot;The World of Warlocks&quot; for details on how
        to become an Ultimate Warlock.
      </p>

      <hr className="my-6 border-border" />

      <h2 className="text-xl font-semibold mt-6 mb-2">Pact Magic</h2>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Otherworldly Gifts</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Most of the power of your character comes from magical gifts bestowed
          upon you by various Patrons. You can earn these Gifts by trading away
          parts of your soul, or by pleasing the Patrons in other ways through
          various in-game events and missions.
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Dark Offerings</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          These represent pieces of your soul you have traded away for power.
          Dark Offerings are purely roleplay effects for you to integrate into
          your character however you wish. Examples may include losing your
          sense of humour, your courage, or your ability to read and write. If
          you ever reach 8 Dark Offerings, your character dies instantly, as
          they have completely given their soul away!
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Patrons</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Magical beings that exist outside of our reality, Patrons can be
          summoned through rituals, and grant power in exchange for souls. Once
          a Patron has granted you a Gift, you can then complete daily Patron
          Challenges unique to them that will restore your MP.
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Ultimate Warlock</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          A mortal being who has complete control over their own soul, yet
          possesses all thirteen Gifts from all thirteen Patrons. Such
          individuals are revered, admired, and feared, wield immense magical
          power, and often rule entire worlds of their own.
        </CardContent>
      </Card>

      <hr className="my-6 border-border" />

      <h2 className="text-xl font-semibold mt-6 mb-2">Status Effects</h2>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Bleeding Out</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          When you reach 0 HP, you begin Bleeding Out. You have 5 minutes before
          you die. Another character with Practical Medicine can stabilise you,
          restoring you to 1 HP. Casting Heal on someone instantly stops them
          Bleeding Out.
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Poisoned</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Your max HP drops to 1. This can be cured with a Long Rest, or by a
          character with the Practical Medicine skill (who must expend 1 silver
          coin on antivenom).
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Invisible</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Represented by crossing your arms in front of you. Invisible
          characters cannot be seen or targeted, but the invisibility ends if
          they attack or cast a spell.
        </CardContent>
      </Card>

      <Badge variant="outline">
        More status effects may apply during specific encounters
      </Badge>
    </RulebookPage>
  );
}
